import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Languages } from 'lucide-react';
import { Button } from './ui/button';
import ThemeToggle from './ThemeToggle';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const LOGO_URL = "/pizzatoons-logo.png";

const Header = ({ isAlwaysScrolled = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t.nav.home, href: '#home' },
    { name: t.nav.story, href: '#story' },
    { name: t.nav.franchise, href: '#franchise' },
    { name: t.nav.faq, href: '#faq' },
    { name: t.nav.outlets, href: '#outlets' },
    { name: t.nav.contact, href: '#contact' }
  ];

  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${(isScrolled || isAlwaysScrolled)
        ? 'bg-white dark:bg-gray-900 shadow-lg py-2'
        : 'bg-transparent py-4'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button onClick={() => scrollToSection('#home')} className="flex items-center gap-2 group">
            <img
              src={LOGO_URL}
              alt="PizzaToons"
              className="h-12 md:h-14 w-auto object-contain transition-all duration-300"
            />
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className={`text-sm font-medium transition-all duration-300 hover:scale-105 ${(isScrolled || isAlwaysScrolled)
                  ? 'text-gray-700 dark:text-gray-200 hover:text-red-600 dark:hover:text-red-400'
                  : 'text-white/90 hover:text-white'
                  }`}
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                {link.name}
              </button>
            ))}
          </nav>

          {/* CTA Button & Toggles */}
          <div className="hidden md:flex items-center gap-3 lg:gap-4">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className={`p-2 rounded-full transition-all duration-300 flex items-center gap-2 text-xs font-bold border ${(isScrolled || isAlwaysScrolled)
                ? 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                : 'border-white/20 text-white hover:bg-white/10 shadow-lg'
                }`}
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <Languages className="w-4 h-4" />
              <span>{language === 'en' ? 'मराठी' : 'English'}</span>
            </button>

            <ThemeToggle transparent={!(isScrolled || isAlwaysScrolled)} />

            <Button
              onClick={() => scrollToSection('#enquiry')}
              className={`transition-all duration-300 font-semibold px-4 py-2 text-sm ${(isScrolled || isAlwaysScrolled)
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : 'bg-yellow-400 hover:bg-yellow-500 text-red-800'
                }`}
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {t.nav.enquiry}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={toggleLanguage}
              className={`p-2 rounded-full border text-xs font-bold flex items-center gap-1 ${(isScrolled || isAlwaysScrolled) ? 'border-gray-200 text-gray-800 dark:border-gray-700 dark:text-white' : 'border-white/20 text-white'}`}
            >
              <Languages className="w-4 h-4" />
              <span>{language === 'en' ? 'मराठी' : 'EN'}</span>
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2"
            >
              {isMobileMenuOpen ? (
                <X className={`w-6 h-6 ${(isScrolled || isAlwaysScrolled) ? 'text-gray-800 dark:text-white' : 'text-white'}`} />
              ) : (
                <Menu className={`w-6 h-6 ${(isScrolled || isAlwaysScrolled) ? 'text-gray-800 dark:text-white' : 'text-white'}`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white dark:bg-gray-900 shadow-lg py-4 px-4 border-t dark:border-gray-800 max-h-[80vh] overflow-y-auto">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="text-gray-700 dark:text-gray-200 hover:text-red-600 dark:hover:text-red-400 text-left font-medium py-2 transition-colors"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  {link.name}
                </button>
              ))}
              <Button
                onClick={() => scrollToSection('#enquiry')}
                className="bg-red-500 hover:bg-red-600 text-white w-full mt-2"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                {t.nav.enquiry}
              </Button>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                <span className="text-sm text-gray-500 dark:text-gray-400">Switch Theme</span>
                <ThemeToggle />
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
